import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useUpdatePasswordMutation, useUpdateProfileMutation, useGetCurrentUserProfileQuery } from '../redux/adminApiSlice';

// Helper function to extract content from privacy-wrapped fields
// Handles both privacy format { content: string, isPrivate: boolean } and plain strings
const extractContent = (field: any): string => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object' && 'content' in field) {
    return field.content || '';
  }
  return String(field);
};

const ProfilePage: React.FC = () => {
  // Password update state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Profile update state
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  
  // Error and success messages
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const [updatePassword, { isLoading: isUpdatingPassword }] = useUpdatePasswordMutation();
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateProfileMutation();
  const { data: userProfile, refetch: refetchProfile } = useGetCurrentUserProfileQuery();

  // Load user profile data
  useEffect(() => {
    if (userProfile) {
      // Extract content from privacy-wrapped fields
      setDisplayName(extractContent(userProfile.displayName));
      setUsername(extractContent(userProfile.username) || userProfile.username || '');
      setEmail(userProfile.email || '');
    }
    // With HttpOnly cookies, we can't decode tokens client-side
    // User info must come from the API response (userProfile)
  }, [userProfile]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      await updatePassword({
        currentPassword,
        newPassword
      }).unwrap();

      setPasswordSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err?.data?.message || err?.message || 'Failed to update password. Please try again.');
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');

    // Validation
    if (!username || !email) {
      setProfileError('Username and email are required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setProfileError('Please enter a valid email address');
      return;
    }

    // Username validation (alphanumeric and underscores, 3-20 chars)
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    if (!usernameRegex.test(username)) {
      setProfileError('Username must be 3-20 characters and contain only letters, numbers, and underscores');
      return;
    }

    try {
      const updateData: { displayName?: string; username?: string; email?: string } = {};
      
      // Compare against extracted content from privacy-wrapped fields
      const currentDisplayName = userProfile ? extractContent(userProfile.displayName) : '';
      const currentUsername = userProfile ? (extractContent(userProfile.username) || userProfile.username || '') : '';
      const currentEmail = userProfile?.email || '';
      
      if (displayName !== currentDisplayName) {
        updateData.displayName = displayName;
      }
      if (username !== currentUsername) {
        updateData.username = username;
      }
      if (email !== currentEmail) {
        updateData.email = email;
      }

      if (Object.keys(updateData).length === 0) {
        setProfileError('No changes to save');
        return;
      }

      await updateProfile(updateData).unwrap();
      setProfileSuccess('Profile updated successfully!');
      refetchProfile();
    } catch (err: any) {
      setProfileError(err?.data?.message || err?.message || 'Failed to update profile. Please try again.');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Profile
        </Typography>

        {/* Profile Update Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Profile Information
          </Typography>

          {profileError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError('')}>
              {profileError}
            </Alert>
          )}

          {profileSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setProfileSuccess('')}>
              {profileSuccess}
            </Alert>
          )}

          <Box component="form" onSubmit={handleProfileSubmit} sx={{ mt: 2 }}>
            <TextField
              margin="normal"
              fullWidth
              name="displayName"
              label="Display Name"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              helperText="Optional: How your name appears to others"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="username"
              label="Username"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              helperText="3-20 characters, letters, numbers, and underscores only"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="email"
              label="Email Address"
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isUpdatingProfile}
            >
              {isUpdatingProfile ? <CircularProgress size={24} /> : 'Update Profile'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Password Update Section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Update Password
          </Typography>

          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>
              {passwordError}
            </Alert>
          )}

          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess('')}>
              {passwordSuccess}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePasswordSubmit} sx={{ mt: 2 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="currentPassword"
              label="Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Must be at least 8 characters long"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isUpdatingPassword}
            >
              {isUpdatingPassword ? <CircularProgress size={24} /> : 'Update Password'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProfilePage;
